import { getFounderAddedCompetitors } from "@/frontend/entities/Founder/model/services/getFounderAddedCompetitors";
import { getLatestRevisionDetails } from "@/frontend/entities/Founder/model/services/getLatestRevisionDetails";
import { getFounderCompanyMarketOverview } from "@/frontend/features/Company/model/getFounderCompanyMarketOverview";
import { getFounderCompanyPatents } from "@/frontend/features/Company/model/getFounderCompanyPatents";
import { getFounderCompanyProducts } from "@/frontend/features/Company/model/getFounderCompanyProducts";
import { getFounderCompanySimilarCompanies } from "@/frontend/features/Company/model/getFounderCompanySimilarCompanies";
import { getFounderCompanyTeam } from "@/frontend/features/Company/model/getFounderCompanyTeam";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { TParsingStatus } from "@/libs/models/portfolio-companies";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function getFounderCompanyData() {
  // latest revision (company details)

  const {
    data: detailsData,
    isLoading: isLoadingDetailsData,
    refetch: refetchDetailsData,
    error: detailsDataError
  } = useQuery({
    queryKey: ["founderCompanyDetails"],
    queryFn: getLatestRevisionDetails,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (detailsDataError) {
      console.error(detailsDataError);
      snackbarConstructor("Error fetching company details", "error");
      reportErrorToSentry(detailsDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/latest");
    }
  }, [detailsDataError]);

  // patents

  const {
    data: patentsData,
    isLoading: isLoadingPatentsData,
    refetch: refetchPatentsData,
    error: patentsDataError
  } = useQuery({
    queryKey: ["founderCompanyPatents"],
    queryFn: getFounderCompanyPatents,

    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (patentsDataError) {
      console.error(patentsDataError);
      snackbarConstructor("Error fetching patents", "error");
      reportErrorToSentry(patentsDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/patents");
    }
  }, [patentsDataError]);

  // team

  const {
    data: teamData,
    isLoading: isLoadingTeamData,
    refetch: refetchTeamData,
    error: teamDataError
  } = useQuery({
    queryKey: ["founderCompanyTeam"],
    queryFn: getFounderCompanyTeam,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (teamDataError) {
      console.error(teamDataError);
      snackbarConstructor("Error fetching team", "error");
      reportErrorToSentry(teamDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/harmonic-people");
    }
  }, [teamDataError]);

  // similar companies

  const [similarCompaniesDataStatus, setSimilarCompaniesDataStatus] = useState<TParsingStatus | null>("processing");
  const {
    data: similarCompaniesData,
    isLoading: isLoadingSimilarCompaniesData,
    refetch: refetchSimilarCompaniesData,
    error: similarCompaniesDataError
  } = useQuery({
    queryKey: ["founderCompanySimilarCompanies"],
    queryFn: async () => {
      const response = await getFounderCompanySimilarCompanies();
      setSimilarCompaniesDataStatus(response.status);
      return response;
    },
    enabled: similarCompaniesDataStatus !== "done",
    refetchInterval: 5000,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (similarCompaniesDataError) {
      console.error(similarCompaniesDataError);
      setSimilarCompaniesDataStatus("done");
      snackbarConstructor("Error fetching similar companies", "error");
      reportErrorToSentry(similarCompaniesDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/similar-companies");
    }
  }, [similarCompaniesDataError]);

  // products

  const [productsDataStatus, setProductsDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: productsData,
    isLoading: isLoadingProductsData,
    refetch: refetchProductsData,
    error: productsDataError
  } = useQuery({
    queryKey: ["founderCompanyProducts"],
    queryFn: async () => {
      const response = await getFounderCompanyProducts();
      setProductsDataStatus(response.status);
      return response;
    },
    enabled: productsDataStatus !== "done",
    refetchInterval: 5000,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (productsDataError) {
      console.error(productsDataError);
      setProductsDataStatus("done");
      snackbarConstructor("Error fetching products", "error");
      reportErrorToSentry(productsDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/products");
    }
  }, [productsDataError]);

  // competitors

  const {
    data: competitorsData,
    isLoading: isLoadingCompetitorsData,
    refetch: refetchCompetitorsData,
    error: competitorsDataError
  } = useQuery({
    queryKey: ["founderCompanyCompetitors"],
    queryFn: getFounderAddedCompetitors,
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (competitorsDataError) {
      console.error(competitorsDataError);
      snackbarConstructor("Error fetching competitors", "error");
      reportErrorToSentry(competitorsDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/competitors");
    }
  }, [competitorsDataError]);

  // market

  const [marketOverviewDataStatus, setMarketOverviewDataStatus] = useState<TParsingStatus | null>("processing");

  const {
    data: marketOverviewData,
    isLoading: isLoadingMarketOverviewData,
    refetch: refetchMarketOverviewData,
    error: marketOverviewDataError
  } = useQuery({
    queryKey: ["founderCompanyMarketOverview"],
    queryFn: async () => {
      const response = await getFounderCompanyMarketOverview();
      setMarketOverviewDataStatus(response.status);

      return response;
    },
    refetchInterval: 5000,
    enabled: marketOverviewDataStatus !== "done" && marketOverviewDataStatus !== "error",
    gcTime: 0,
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (marketOverviewDataError) {
      console.error(marketOverviewDataError);
      setMarketOverviewDataStatus("done");
      snackbarConstructor("Error fetching market overview", "error");
      reportErrorToSentry(marketOverviewDataError, "FOUNDER COMPANY PROFILE: /founder-revisions/market-overview");
    }
  }, [marketOverviewDataError]);

  return {
    detailsData,
    isLoadingDetailsData,
    refetchDetailsData,
    similarCompaniesData,
    isLoadingSimilarCompaniesData,
    refetchSimilarCompaniesData,
    patentsData,
    isLoadingPatentsData,
    refetchPatentsData,
    productsData,
    isLoadingProductsData,
    refetchProductsData,
    competitorsData,
    isLoadingCompetitorsData,
    refetchCompetitorsData,
    teamData,
    isLoadingTeamData,
    refetchTeamData,
    marketOverviewData,
    isLoadingMarketOverviewData,
    refetchMarketOverviewData,
    similarCompaniesDataStatus,
    productsDataStatus,
    marketOverviewDataStatus
  };
}
